/** BOM workflow types — determines which BOM sub-state is active */
export const BOM_TYPES = {
  STANDARD: 'standard',
  LGCY: 'lgcy',
} as const;

export type BomType = typeof BOM_TYPES[keyof typeof BOM_TYPES];

export const DEFAULT_BOM_TYPE: BomType = BOM_TYPES.STANDARD;

export const ESS_CONFIG_OPTIONS = [
  { value: 'grid_tied', label: 'Grid Type/PV Only' },
  { value: 'partial_home', label: 'Partial Home' },
  { value: 'whole_home_backup', label: 'Whole Home Backup' },
] as const;

export type EssConfigValue = typeof ESS_CONFIG_OPTIONS[number]['value'];

export const INSTALLATION_METHOD_OPTIONS = [
  { value: 'rafter_mount', label: 'Rafter Mount' },
  { value: 'deck_mount', label: 'Deck Mount' },
] as const;

export type InstallationMethod = typeof INSTALLATION_METHOD_OPTIONS[number]['value'];

export const INSTALLATION_METHODS = {
  RAFTER_MOUNT: 'rafter_mount' as InstallationMethod,
  DECK_MOUNT: 'deck_mount' as InstallationMethod,
};

export const DEFAULT_INSTALLATION_METHOD: InstallationMethod = INSTALLATION_METHODS.RAFTER_MOUNT;

/** Item types that are always auto-enabled (checked) when a package is selected */
export const AUTO_ENABLED_ITEM_TYPES = ['pv-module', 'inverter'] as const;

/** Column names of the NetSuite BOM export CSV — shared by export generation and re-import parsing */
export const BOM_CSV_COLUMNS = {
  ITEM_ID: 'Item ID',
  QTY: 'Qty',
  LIS_NUMBER: 'LIS#',
  ITEM: 'Item',
  PACKAGE_KEY: 'Package Key',
  ESS_CONFIG: 'ESS Config',
} as const;

/** Ordered header row of the NetSuite BOM export CSV */
export const BOM_CSV_HEADERS = [
  BOM_CSV_COLUMNS.ITEM_ID,
  BOM_CSV_COLUMNS.QTY,
  BOM_CSV_COLUMNS.LIS_NUMBER,
  BOM_CSV_COLUMNS.ITEM,
  BOM_CSV_COLUMNS.PACKAGE_KEY,
  BOM_CSV_COLUMNS.ESS_CONFIG,
] as const;

/** BOM CSV re-import configuration and user feedback messages */
export const BOM_CSV_IMPORT = {
  FILE_ACCEPT: '.csv,text/csv',
  GENERIC_ERROR_MESSAGE: 'Failed to import BOM CSV',
  PACKAGE_NOT_FOUND_MESSAGE: 'No lender package found for the package key in this CSV',
  UNMATCHED_TOAST_DURATION_MS: 6000,
} as const;

/** Color scheme per item_type for lender package table display */
export const PACKAGE_ITEM_TYPE_COLORS: Record<string, { badge: string; bg: string; border: string }> = {
  'pv-module':     { badge: 'text-amber-700 bg-amber-50 border-amber-200',      bg: 'from-amber-50/60 to-amber-50/20',     border: 'border-l-amber-400' },
  'racking':       { badge: 'text-blue-700 bg-blue-50 border-blue-200',         bg: 'from-blue-50/60 to-blue-50/20',       border: 'border-l-blue-400' },
  'attachment':    { badge: 'text-emerald-700 bg-emerald-50 border-emerald-200', bg: 'from-emerald-50/60 to-emerald-50/20', border: 'border-l-emerald-400' },
  'inverter':      { badge: 'text-violet-700 bg-violet-50 border-violet-200',   bg: 'from-violet-50/60 to-violet-50/20',   border: 'border-l-violet-400' },
  'storage-unit':  { badge: 'text-rose-700 bg-rose-50 border-rose-200',         bg: 'from-rose-50/60 to-rose-50/20',       border: 'border-l-rose-400' },
  'optimizer-rsd': { badge: 'text-cyan-700 bg-cyan-50 border-cyan-200',         bg: 'from-cyan-50/60 to-cyan-50/20',       border: 'border-l-cyan-400' },
};

export const DEFAULT_PACKAGE_ITEM_COLORS = {
  badge: 'text-slate-600 bg-slate-100 border-slate-200',
  bg: 'from-slate-50/60 to-slate-50/20',
  border: 'border-l-slate-300',
};

/** Fixed display order for item type groups */
export const PACKAGE_ITEM_TYPE_PRIORITY: Record<string, number> = {
  'pv-module': 0,
  'inverter': 1,
  'storage-unit': 2,
  'optimizer-rsd': 3,
  'attachment': 4,
  'racking': 5,
};
